import React, { useEffect, useRef, useState } from 'react';
import { useSubscription } from '../../hooks/useFeature';
import FeatureBlockedPage from '../FeatureBlockedPage';

export interface SubscriptionFeatureGuardProps {
  /** The subscription feature code (e.g. 'library_management').
   *  Title and description are resolved from the feature registry automatically. */
  featureCode: string;
  /** Optional override for the lock title shown on the blocked overlay. */
  featureName?: string;
  /** Optional override for the lock description shown on the blocked overlay. */
  description?: string;
  children: React.ReactNode;
}

const SubscriptionFeatureGuard: React.FC<SubscriptionFeatureGuardProps> = ({
  featureCode,
  featureName,
  description,
  children,
}) => {
  const { subscription, loading, hasFeature, refreshSubscription } = useSubscription();
  const [ready, setReady] = useState(false);
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    if (subscription !== null) {
      // Already have cached subscription data — ready immediately
      setReady(true);
    } else {
      // No cached data — fetch and wait before rendering anything
      refreshSubscription().finally(() => setReady(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Block render until we know the subscription state
  if (!ready || loading) return null;

  // No subscription loaded (user has no billing privilege) — allow through, backend handles it
  if (!subscription) {
    return <>{children}</>;
  }

  const hasAccess = hasFeature(featureCode);

  if (!hasAccess) {
    return (
      <div className="relative min-h-full h-full">
        {/* Do NOT render children — prevents API calls that would fire 403s
            while the overlay is already communicating the feature restriction */}
        <div className="min-h-[400px] bg-gradient-to-br from-gray-50 to-gray-100" />
        <FeatureBlockedPage
          featureCode={featureCode}
          featureName={featureName}
          description={description}
        />
      </div>
    );
  }

  return <>{children}</>;
};

export default SubscriptionFeatureGuard;
